/**
 * Camel Up visual language — "dusk in the bazaar".
 *
 * Aesthetic direction: a warm desert at golden hour. Sand-gradient surfaces,
 * brass/gold hardware, papyrus panels, and tactile game pieces (pip dice,
 * perforated betting slips, coins). Backgrounds layer gradients + a faint
 * grain so nothing reads as flat AI-default.
 *
 * All background tones use `light-dark()` so they track the global Mantine
 * color scheme (same approach Scotland Yard uses). Camel + accent colors are
 * semantic and identical in both modes.
 */

import type { CamelColor } from '@mdev/common'

// --- Fonts -----------------------------------------------------------------

/** Chunky vintage-poster slab — wordmark + big numerals (coins, dice values). */
export const CU_DISPLAY = '"Alfa Slab One", "Fraunces", Georgia, serif'
/** Warm high-contrast serif — panel titles, ranks, labels. */
export const CU_HEADING = '"Fraunces", Georgia, "Times New Roman", serif'

// --- Grain overlay (tiny inline SVG noise, tiled) --------------------------

const GRAIN_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/></filter><rect width="120" height="120" filter="url(#n)" opacity="0.5"/></svg>`,
)
const GRAIN_URL = `url("data:image/svg+xml,${GRAIN_SVG}")`

export const CU = {
  /**
   * Full-bleed page background: a dusk-desert sky→sand wash with a warm sun
   * glow in the upper right and a faint grain texture on top. Two values —
   * light = golden afternoon, dark = deep desert night.
   */
  pageBg:
    'light-dark(' +
    `${GRAIN_URL}, ` +
    'radial-gradient(120% 80% at 85% -10%, rgba(255, 214, 138, 0.85) 0%, rgba(255, 184, 110, 0) 55%), ' +
    'linear-gradient(180deg, #ffe6b0 0%, #f7c277 38%, #e89b5a 100%)' +
    ', ' +
    `${GRAIN_URL}, ` +
    'radial-gradient(120% 80% at 85% -10%, rgba(214, 142, 64, 0.5) 0%, rgba(120, 70, 30, 0) 55%), ' +
    'linear-gradient(180deg, #2a1d0e 0%, #1d1408 55%, #150f06 100%)' +
    ')',
  /** Slightly inset section background behind grouped panels. */
  sectionBg: 'light-dark(rgba(255, 247, 224, 0.35), rgba(20, 14, 6, 0.4))',
  /** Papyrus panel surface. */
  panelBg: 'light-dark(#fdf3da, #2b2010)',
  /** Lighter inset surface (cards, rows). */
  surfaceBg: 'light-dark(rgba(255, 250, 240, 0.7), rgba(56, 41, 19, 0.5))',
  /** Solid inset surface where transparency would muddy text. */
  surfaceSolid: 'light-dark(#fffaf0, #3a2b15)',
  /** Brass hairline border. */
  border: 'light-dark(#d9a441, #6e4e16)',
  /** Soft border for nested rows. */
  borderSoft: 'light-dark(rgba(217, 164, 65, 0.35), rgba(110, 78, 22, 0.55))',
  /** Brass hardware accent (rings, rules, numerals). */
  brass: 'light-dark(#b5791f, #d6a54f)',
  /** Lighter brass for highlights. */
  brassSoft: 'light-dark(#e4c178, #8a6320)',
  /** Hot accent — "your turn", primary CTAs (a spiced rose-red). */
  accent: 'light-dark(#d23b52, #f0566c)',
  accentDeep: 'light-dark(#a82740, #c23350)',
  /** Track (paving-stone) base. */
  trackBg: 'light-dark(#e7b463, #4a3216)',
  trackBgAlt: 'light-dark(#edc079, #543a1c)',
  /** Track inner shadow / groove. */
  trackGroove: 'light-dark(rgba(120, 72, 20, 0.35), rgba(0, 0, 0, 0.45))',
  /** Ink for text on papyrus. */
  ink: 'light-dark(#3a2a14, #f3e4c6)',
  inkSoft: 'light-dark(#7a5a2e, #c7a96f)',
  /** Panel drop shadow. */
  shadow: 'light-dark(0 8px 24px rgba(120, 72, 20, 0.18), 0 8px 24px rgba(0, 0, 0, 0.5))',
  shadowSoft: 'light-dark(0 2px 8px rgba(120, 72, 20, 0.14), 0 2px 8px rgba(0, 0, 0, 0.4))',
  /** Error surfaces. */
  dangerBg: 'light-dark(#fbe2e2, #5e1a1a)',
  dangerBorder: 'light-dark(#e0a3a3, #b3413f)',
  /** Coin gold. */
  coin: '#E8B43A',
  coinDeep: '#A9781C',
} as const

/**
 * Per-camel color ramp. `body` is the primary fill, `dark` the shaded edge
 * (used as the bottom gradient stop), `accent` the saddle/leg detail. The
 * white camel gets a visible `dark` outline so it reads on light papyrus.
 */
export const CAMEL_HEX: Record<
  CamelColor,
  { body: string; light: string; dark: string; accent: string; ink: string }
> = {
  red: { body: '#E5392A', light: '#F26152', dark: '#A81E13', accent: '#7E140B', ink: '#fff' },
  yellow: { body: '#F6B800', light: '#FFD23F', dark: '#C98A00', accent: '#8A5E00', ink: '#3a2a14' },
  green: { body: '#1DA64A', light: '#3FCB6C', dark: '#0E7232', accent: '#0A4E22', ink: '#fff' },
  blue: { body: '#2A8CF0', light: '#5BACFF', dark: '#1463B8', accent: '#0C3F78', ink: '#fff' },
  purple: { body: '#9B30D4', light: '#BC5BEB', dark: '#6E18A8', accent: '#4A1070', ink: '#fff' },
  black: { body: '#3b3b40', light: '#5a5a62', dark: '#1c1c20', accent: '#0e0e12', ink: '#fff' },
  white: { body: '#f3ede1', light: '#ffffff', dark: '#cfc6b4', accent: '#9c9486', ink: '#3a2a14' },
}
